import React, { useEffect } from 'react';
import { X } from '@phosphor-icons/react';
import { cn } from '../../lib/utils';

/** Brand-aligned modal shell for create/edit/confirm flows. */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  className,
}) {
  useEffect(() => {
    if (!open) return undefined;
    function handleKey(event) {
      if (event.key === 'Escape') onClose?.();
    }
    document.addEventListener('keydown', handleKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  const widthClass =
    size === 'sm' ? 'max-w-md' : size === 'lg' ? 'max-w-2xl' : 'max-w-xl';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/40"
        aria-label="Close dialog"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="vi-modal-title"
        className={cn(
          'relative z-10 flex max-h-[90vh] w-full flex-col overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-[0_16px_48px_rgba(15,23,42,0.18)]',
          widthClass,
          className
        )}
      >
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3.5 sm:px-5">
          <div className="min-w-0">
            <h2 id="vi-modal-title" className="font-display text-sm font-bold text-[#201C35]">
              {title}
            </h2>
            {description ? <p className="mt-0.5 text-xs text-slate-600">{description}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close"
          >
            <X size={16} weight="bold" aria-hidden="true" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5">{children}</div>
        {footer ? (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 px-4 py-3 sm:px-5">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function ModalActionButton({
  children,
  variant = 'primary',
  disabled,
  onClick,
  type = 'button',
  // FIX: this was missing. AccountsPage.jsx renders
  // <ModalActionButton type="submit" form="create-tenant-form">, relying
  // on the HTML `form` attribute to associate a submit button with a
  // <form> it isn't nested inside (the button lives in the Modal's
  // `footer` slot, a separate part of the DOM tree from the form in
  // `children`). Without destructuring and forwarding `form` here, React
  // silently dropped it, so the rendered <button type="submit"> had no
  // form association at all — clicking it did nothing, since it had no
  // onClick either (submission was meant to happen purely via `form`).
  form,
  className,
}) {
  const variants = {
    primary:
      'bg-[var(--brand-primary,#1A428A)] text-white shadow-md hover:bg-[#163872] hover:shadow-lg',
    secondary: 'bg-slate-100 text-slate-700 shadow-sm hover:bg-slate-200',
    outline: 'border border-slate-200 bg-white text-slate-800 shadow-sm hover:bg-slate-50',
    danger: 'bg-rose-600 text-white shadow-md hover:bg-rose-700',
  };

  return (
    <button
      type={type}
      form={form}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'inline-flex h-9 items-center justify-center rounded-full px-4 text-sm font-semibold transition duration-150',
        'hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary,#1A428A)]/20',
        'disabled:pointer-events-none disabled:opacity-50 disabled:hover:scale-100',
        variants[variant],
        className
      )}
    >
      {children}
    </button>
  );
}